import { useEffect } from "react";
import * as React from "react";
import { useIonRouter } from "@/app/IonicContextProvider";
import { useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { MessageSquare } from "lucide-react";
import { IonPage, IonContent, IonHeader, IonToolbar, IonTitle, IonItem, IonLabel, IonInput, IonButton, IonIcon, IonImg } from '@ionic/react';
import iconImage from "@/assets/images/icon.png";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { authLoginRequestSchema } from "@/shared/zod";
import { extractErrorMessage } from "@/shared/utils/errors";
import { useLogin } from "../hooks";
import { useAuthContext } from "@/lib/authContext";
import { storageService, STORAGE_KEYS } from "@/lib/storage";
import type { AuthLoginRequest } from "../types";

export function LoginPage() {
  const router = useIonRouter();
  const location = useLocation();
  const { toast } = useToast();
  const loginMutation = useLogin();
  const { isAuthenticated, isInitialized } = useAuthContext();

  // Belt-and-braces redirect - even if route logic fails, bounce out as soon as auth flips
  useEffect(() => {
    const path = location?.pathname ?? '/';
    if (location && isAuthenticated && (path === '/login' || path === '/')) {
      router.push('/shifts', 'root', 'replace');
    }
  }, [isAuthenticated, location?.pathname, router]);

  // Redirect if already authenticated
  if (isInitialized && isAuthenticated) {
    router.push("/shifts", "root", "replace");
    return null;
  }

  const [hasStoredPhone, setHasStoredPhone] = React.useState(false);

  const form = useForm<AuthLoginRequest>({
    resolver: zodResolver(authLoginRequestSchema),
    defaultValues: {
      whatsapp_number: "",
      pin_code: "",
    },
  });

  // Load saved phone number on component mount
  useEffect(() => {
    const loadSavedPhoneNumber = async () => {
      try {
        const savedPhoneNumber = await storageService.getPhoneNumber();
        if (savedPhoneNumber) {
          form.setValue("whatsapp_number", savedPhoneNumber);
          setHasStoredPhone(true);
        }
      } catch (error) {
        console.error("Error loading saved phone number:", error);
      }
    };

    loadSavedPhoneNumber();
  }, [form]);

  const onSubmit = async (data: AuthLoginRequest) => {
    try {
      await loginMutation.mutateAsync(data);
    } catch (error) {
      toast({
        title: "Login Failed",
        description: extractErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  return (
    <IonPage>
      <IonContent className="ion-padding">
        <div className="min-h-screen bg-muted/30" data-testid="login-page">
          <div className="flex min-h-screen">
            <div className="flex-1 flex flex-col justify-center px-4 sm:px-6 lg:px-20 xl:px-24">
              <div className="mx-auto w-full max-w-sm lg:w-96">
                <div className="text-center mb-8">
                  <div className="mx-auto h-32 w-32 bg-white rounded-xl flex items-center justify-center mb-4">
                    <IonImg src={iconImage} />
                  </div>
                  <h2 className="text-3xl font-bold tracking-tight text-foreground">
                    Welcome back
                  </h2>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Sign in to your TightShip account
                  </p>
                </div>

                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  {!hasStoredPhone && (
                    <div>
                      <Label htmlFor="login-whatsapp">Phone Number</Label>
                      <Input
                        id="login-whatsapp"
                        type="tel"
                        placeholder="+254712345678"
                        {...form.register("whatsapp_number")}
                        data-testid="input-whatsapp-number"
                      />
                      {form.formState.errors.whatsapp_number && (
                        <p className="mt-1 text-xs text-destructive">
                          {form.formState.errors.whatsapp_number.message}
                        </p>
                      )}
                    </div>
                  )}

                  {hasStoredPhone && (
                    <div className="text-center mb-4">
                      <p className="text-sm text-muted-foreground">
                        Logging in as: <span className="font-medium">{form.watch("whatsapp_number")}</span>
                      </p>
                      <button
                        type="button"
                        onClick={async () => {
                          await storageService.remove(STORAGE_KEYS.PHONE_NUMBER);
                          form.setValue("whatsapp_number", "");
                          setHasStoredPhone(false);
                        }}
                        className="text-xs text-primary hover:text-primary/80 mt-2"
                      >
                        Use different number
                      </button>
                    </div>
                  )}

                  <div>
                    <Label htmlFor="login-pin">PIN</Label>
                    <Input
                      id="login-pin"
                      type="password"
                      maxLength={4}
                      placeholder="••••"
                      className="text-center text-2xl tracking-widest"
                      {...form.register("pin_code")}
                      data-testid="input-pin-code"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      autoFocus={hasStoredPhone}
                    />
                    {form.formState.errors.pin_code && (
                      <p className="mt-1 text-xs text-destructive">
                        {form.formState.errors.pin_code.message}
                      </p>
                    )}
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white text-sm font-medium px-4 py-2 rounded-lg shadow-sm transition-colors tap-highlight-transparent"
                    disabled={loginMutation.isPending}
                    data-testid="button-login"
                  >
                    {loginMutation.isPending ? "Signing In..." : "Sign In"}
                  </Button>
                </form>

                <div className="mt-6 text-center">
                  <p className="text-sm text-muted-foreground">
                    Don't have an account? Ask your employer to create one for you.
                  </p>
                </div>
              </div>
            </div>

            <div className="hidden lg:block relative w-0 flex-1">
              <div className="absolute inset-0 h-full w-full bg-gradient-to-br from-primary via-primary/80 to-primary/60">
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center text-white">
                  <h3 className="text-3xl font-bold mb-4">Your Tasks Await</h3>
                  <p className="text-lg opacity-90 max-w-md">
                    Continue where you left off and stay connected with your team.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
}
